import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { useNavigate } from "react-router-dom";
import { intervalToDuration } from "date-fns";

// icons
import {
  TravelStatusProgrammed,
  TravelStatusPreparing,
  TravelStatusInTransit,
  TravelStatusInConstruction,
  TravelStatusDischarging,
  TravelStatusDelivered,
  TravelStatusCanceled,
  InvoiceInstallmentStatus
} from "@assets/icons/index";

// libraries
import { ArrowForwardIos } from "@libraries/icons";

/** Helpers */
import utils from "@helpers/utils";

/** Graphql */
import {
  ETravelStatus,
  TravelType
} from "@store/graphql/helpers/types.graphql";

import * as Styled from "./styles";

interface LastConcretingProps {
  travel: TravelType;
}

interface IConcretingStatus {
  [key: string]: {
    icon: JSX.Element;
    color: string;
    label: string;
    prefix: string;
  };
}

const LastConcreting: React.FC<LastConcretingProps> = ({ travel }) => {
  const navigate = useNavigate();
  const [nowDate, setNowDate] = useState(new Date());

  const handleNavigate = useCallback(() => {
    navigate("/concretings", {
      state: {
        travel
      }
    });
  }, [travel, navigate]);

  const timeFormatted = useMemo(() => {
    const { status } = travel;

    let timeStatus;

    switch (status) {
      case ETravelStatus.INVOICE_ISSUED:
        if (travel.invoiceIssueTime) {
          timeStatus = utils.formatDateIfHave(
            new Date(travel.invoiceIssueTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.IN_TRANSIT:
        if (travel.plantDepartureTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.plantDepartureTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.IN_CONSTRUCTION:
        if (travel.constructionStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.constructionStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.DISCHARGING:
        if (travel.dischargeStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.dischargeStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }

        break;
      case ETravelStatus.DELIVERED:
        if (travel.plantDepartureTime) {
          const startTime = new Date(
            travel.plantDepartureTime ?? travel.invoiceIssueTime
          );
          let endTime = new Date(
            travel.invoiceIssueTime ?? travel.plantDepartureTime
          );

          if (travel.dischargeEndTime) {
            endTime = new Date(travel.dischargeEndTime);
          } else if (travel.constructionEndTime) {
            endTime = new Date(travel.constructionEndTime);
          } else if (travel.finishTime) {
            endTime = new Date(travel.finishTime);
          }

          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: startTime,
              end: endTime
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.PROGRAMMED:
        if (travel.scheduledTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: nowDate,
              end: new Date(travel.scheduledTime)
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      default:
        break;
    }

    return timeStatus;
  }, [nowDate, travel]);

  const concretingStatus = useMemo(() => ({
    [ETravelStatus.PROGRAMMED]: {
      icon: <ReactSVG src={TravelStatusProgrammed} />,
      color: "#64DD17",
      label: "Entrega prevista ",
      prefix: "daqui à "
    },
    [ETravelStatus.PREPARING]: {
      icon: <ReactSVG src={TravelStatusPreparing} />,
      color: "#ED7117",
      label: "Entrega aguardando liberação ",
      prefix: ""
    },
    [ETravelStatus.IN_TRANSIT]: {
      icon: <ReactSVG src={TravelStatusInTransit} />,
      color: "#7E73F8",
      label: "A caminho ",
      prefix: "há "
    },
    [ETravelStatus.IN_CONSTRUCTION]: {
      icon: <ReactSVG src={TravelStatusInConstruction} />,
      color: "#C7B301",
      label: "Na obra ",
      prefix: "há "
    },
    [ETravelStatus.DISCHARGING]: {
      icon: <ReactSVG src={TravelStatusDischarging} />,
      color: "#616161",
      label: "Descarregando ",
      prefix: "há "
    },
    [ETravelStatus.DELIVERED]: {
      icon: <ReactSVG src={TravelStatusDelivered} />,
      color: "#64DD17",
      label: "Duração da entrega ",
      prefix: ""
    },
    [ETravelStatus.CANCELED]: {
      icon: <ReactSVG src={TravelStatusCanceled} />,
      color: "#FF0000",
      label: "Entrega cancelada ",
      prefix: ""
    },
    [ETravelStatus.INVOICE_ISSUED]: {
      icon: (
        <Styled.IconContainer color="#64DD17">
          <ReactSVG src={InvoiceInstallmentStatus} />
        </Styled.IconContainer>
      ),
      color: "#64DD17",
      label: "Nota emitida ",
      prefix: " às "
    }
  } as IConcretingStatus), []);

  const infoDetailsDescription = useMemo(() => {
    const concreteMixerDescription = travel.concreteMixer?.code && `${travel.concreteMixer?.code}`;
    const documentNumberDescription = travel.documentNumber && `${travel.documentNumber}`;

    return (
      <>
        {documentNumberDescription && (
          <Styled.InfoDetailItem>
            <span className="title">Remessa:</span> {documentNumberDescription}
          </Styled.InfoDetailItem>
        )}
        {concreteMixerDescription && (
          <Styled.InfoDetailItem>
            <span className="title">Equipamento:</span>{" "}
            {concreteMixerDescription}
          </Styled.InfoDetailItem>
        )}
      </>
    );
  }, [travel]);

  const timeDetail = useMemo(() => {
    const { status } = travel;

    if (
      status === "PROGRAMMED"
      && nowDate.getTime() > new Date(travel.scheduledTime).getTime()
    ) {
      return `para ${timeFormatted} atrás`;
    }

    return `${concretingStatus[status].prefix} ${timeFormatted || ""}`;
  }, [concretingStatus, nowDate, timeFormatted, travel]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowDate(new Date());
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Styled.Container onClick={handleNavigate}>
      <Styled.Info>
        {concretingStatus[travel.status].icon}
        <Styled.InfoDescritpion>
          <Styled.InfoStatus color={concretingStatus[travel.status].color}>
            {concretingStatus[travel.status].label}
          </Styled.InfoStatus>
          {timeDetail || ""}

          <Styled.InfoDetails>{infoDetailsDescription}</Styled.InfoDetails>
        </Styled.InfoDescritpion>
      </Styled.Info>

      <Styled.IconActionContainer>
        <ArrowForwardIos />
      </Styled.IconActionContainer>
    </Styled.Container>
  );
};

export default LastConcreting;
